import { useQuery } from '@tanstack/react-query';

import { useRouter } from '@boss/hooks';
import type { DeliveryMethod, StockStatusLabelTranslations, StockStatusResult } from '@boss/services/client';
import { IArticleStock } from '@boss/types/b2b-b2c';

import { fetchStockByWarehouseIds, fetchStockStatus } from './connector';

import { stockKeys } from '.';

export interface Sku {
  skuid: string;
  quantity?: number;
  unit?: string;
}

export interface StockStatusQueryArgs {
  skus: Sku[];
  deliveryMethod: DeliveryMethod;
  shopWarehouseCode?: string | null;
  locale: string;
  translations: StockStatusLabelTranslations;
}

const useStockByWarehouseIds = ({ warehouseIds, skus }: { warehouseIds: string[]; skus: Sku[] }, enabled: boolean) => {
  const { locale } = useRouter();

  return useQuery<IArticleStock | undefined>({
    queryKey: stockKeys.stockByWarehouseIds({ locale, warehouseIds, skus }),
    queryFn: async () => await fetchStockByWarehouseIds({ warehouseIds, skus, locale: locale as string }),
    enabled: !!enabled,
  });
};

/**
 * Single stock-status check, shared by the PDP presence check and the per-line quantity checks.
 * Exposed as options so the PDP hook can run one query per line via `useQueries`.
 */
const stockStatusQuery = (
  { skus, deliveryMethod, shopWarehouseCode, locale, translations }: StockStatusQueryArgs,
  enabled = true,
) => ({
  queryKey: stockKeys.stockStatus({ skus, deliveryMethod, shopWarehouseCode, locale }),
  // Connector failures resolve undefined (captured in Sentry); keep the result shape valid for React Query.
  queryFn: async () =>
    (await fetchStockStatus({ skus, deliveryMethod, shopWarehouseCode, locale, translations })) ?? {
      lines: [],
      summary: null,
    },
  enabled,
});

const useStockStatus = (args: StockStatusQueryArgs, options?: { enabled?: boolean }) =>
  useQuery<StockStatusResult | undefined>(stockStatusQuery(args, options?.enabled ?? true));

export { useStockByWarehouseIds, useStockStatus, stockStatusQuery };
